/* ==========================================================================
   BOCA LIGHTBOX
   Click-to-zoom for the homepage content images (product showcase, philosophy
   band, packaging band). Self-contained and additive:
     - marks the relevant images as zoomable (no Liquid changes needed);
     - opens a lightweight, accessible modal on click;
     - closes on the × button, the Escape key, or a click outside the image.
   Binds through event delegation, so images added by the theme editor work too.
   No dependencies. Loaded deferred on the homepage and product pages (see layout/theme.liquid).
   ========================================================================== */
(function () {
  'use strict';
  if (window.__bocaLightbox) return;
  window.__bocaLightbox = true;

  /* Content images we want zoomable — deliberately scoped to the homepage
     content bands plus the product hero main image, so navigation, logo and
     icon images are never touched. The homepage hero is a CSS background, not
     an <img>, so it is intentionally left out. */
  var TARGETS = [
    '.bc-hero__frame img',
    '.bc-show__media img',
    '.bc-show__thumb img',
    '.bc-story__media img',
    '.bc-pack__media img'
  ].join(', ');

  /* ---- Styles (injected once, keeps this a single drop-in file) ---- */
  function injectStyles() {
    if (document.getElementById('bc-lightbox-styles')) return;
    var css = '' +
      '.bc-zoomable{cursor:zoom-in;}' +
      '.bc-lb{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;' +
        'justify-content:center;padding:clamp(16px,4vw,48px);background:rgba(28,17,12,.92);' +
        'opacity:0;visibility:hidden;transition:opacity .28s ease,visibility .28s ease;}' +
      '.bc-lb.is-open{opacity:1;visibility:visible;}' +
      '.bc-lb__img{max-width:92vw;max-height:90vh;object-fit:contain;border-radius:2px;' +
        'box-shadow:0 30px 90px -20px rgba(0,0,0,.7);transform:scale(.96);' +
        'transition:transform .28s ease;cursor:zoom-out;}' +
      '.bc-lb.is-open .bc-lb__img{transform:scale(1);}' +
      '.bc-lb__close{position:absolute;top:clamp(12px,2.5vw,28px);right:clamp(12px,2.5vw,28px);' +
        'width:46px;height:46px;display:flex;align-items:center;justify-content:center;' +
        'background:rgba(248,243,234,.10);border:1px solid rgba(248,243,234,.35);' +
        'border-radius:50%;color:#F8F3EA;font-size:26px;line-height:1;cursor:pointer;' +
        'transition:background .25s ease,border-color .25s ease;}' +
      '.bc-lb__close:hover{background:rgba(248,243,234,.20);border-color:#F8F3EA;}' +
      '@media (prefers-reduced-motion:reduce){.bc-lb,.bc-lb__img{transition:none;}}';
    var style = document.createElement('style');
    style.id = 'bc-lightbox-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /* ---- Mark the target images so they read as interactive ---- */
  function markImages() {
    var imgs = document.querySelectorAll(TARGETS);
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.closest('a, button')) continue; // never hijack a linked image
      img.classList.add('bc-zoomable');
      if (!img.hasAttribute('tabindex')) img.setAttribute('tabindex', '0');
      img.setAttribute('role', 'button');
    }
  }

  /* ---- Build a high-resolution URL from an image's current source ---- */
  function fullUrl(img) {
    var src = img.getAttribute('data-bc-zoom-full') || img.currentSrc || img.src;
    try {
      var url = new URL(src, window.location.href);
      url.searchParams.set('width', '1800');
      url.searchParams.delete('height');
      return url.href;
    } catch (e) {
      return src;
    }
  }

  /* ---- The modal ---- */
  var overlay, imgEl, closeBtn, lastFocused;

  function build() {
    overlay = document.createElement('div');
    overlay.className = 'bc-lb';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-hidden', 'true');

    closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'bc-lb__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';

    imgEl = document.createElement('img');
    imgEl.className = 'bc-lb__img';
    imgEl.alt = '';

    overlay.appendChild(closeBtn);
    overlay.appendChild(imgEl);
    document.body.appendChild(overlay);

    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target === imgEl) close();
    });
  }

  function open(src, alt) {
    if (!overlay) build();
    lastFocused = document.activeElement;
    imgEl.src = src;
    imgEl.alt = alt || '';
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () { overlay.classList.add('is-open'); });
    closeBtn.focus();
  }

  function close() {
    if (!overlay || !overlay.classList.contains('is-open')) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(function () { imgEl.src = ''; }, 300);
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  /* ---- Wiring ---- */
  function activate(img) {
    open(fullUrl(img), img.getAttribute('alt'));
  }

  document.addEventListener('click', function (e) {
    var img = e.target.closest ? e.target.closest('img.bc-zoomable') : null;
    if (!img || img.closest('a, button')) return;
    e.preventDefault();
    activate(img);
  });

  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Enter' || e.key === ' ') && e.target && e.target.matches && e.target.matches('img.bc-zoomable')) {
      e.preventDefault();
      activate(e.target);
      return;
    }
    if (e.key === 'Escape') close();
  });

  function init() {
    injectStyles();
    markImages();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-mark images when a section is re-rendered in the theme editor.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', markImages);
  }
})();
